import { useRef, useEffect, useState } from 'react';

const steps = [
  {
    num: '01',
    icon: 'ri-phone-line',
    title: 'お電話・フォームでご連絡',
    desc: 'お電話またはWEBフォームからご連絡ください。買取品目やご希望日時をお伺いします。',
  },
  {
    num: '02',
    icon: 'ri-car-line',
    title: 'スタッフがご自宅へ伺います',
    desc: '出張費は完全無料。ご指定の場所までスタッフが伺います。福岡・九州全域に対応。',
  },
  {
    num: '03',
    icon: 'ri-search-eye-line',
    title: 'その場で丁寧に査定',
    desc: '専門知識を持つスタッフが真摯に査定。査定結果はその場でご提示いたします。',
  },
  {
    num: '04',
    icon: 'ri-money-cny-circle-line',
    title: '即日現金でお支払い',
    desc: 'ご納得いただけた場合、その場で現金をお支払い。お振込みにも対応可能です。',
  },
];

const features = [
  { icon: 'ri-home-heart-line', label: '出張費・査定費完全無料' },
  { icon: 'ri-time-line', label: '即日対応・土日祝も可' },
  { icon: 'ri-hand-heart-line', label: '遺品整理・生前整理対応' },
  { icon: 'ri-shield-check-line', label: '古物商許可証取得済み' },
  { icon: 'ri-money-cny-box-line', label: '現金即日お支払い' },
  { icon: 'ri-map-pin-line', label: '福岡・九州全域に出張対応' },
];

export default function Service() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.2 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="service" className="py-24 bg-stone-900">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-16">
          <span className={`text-amber-400 text-sm font-semibold tracking-widest uppercase mb-3 block transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            SERVICE
          </span>
          <h2
            className={`text-4xl md:text-5xl font-black text-white mb-4 transition-all duration-700 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ fontFamily: '\'Noto Serif JP\', serif' }}
          >
            福岡発・九州全域<br />出張買取サービス
          </h2>
          <p className={`text-stone-400 text-base max-w-lg mx-auto leading-relaxed transition-all duration-700 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            福岡を拠点に九州全域のご自宅まで無料でお伺いし、その場で査定・現金買取いたします。
            遺品整理や大量のお品物にも丁寧に対応いたします。
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {steps.map((step, i) => (
            <StepCard key={step.num} step={step} index={i} />
          ))}
        </div>

        {/* Features + Image */}
        <div className="flex flex-col lg:flex-row gap-12 items-center">
          {/* Image */}
          <div className={`w-full lg:w-[45%] transition-all duration-700 ${titleVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/902f5a13be376692b53a82ab77201156.jpg"
                alt="天神屋スタッフによる出張買取・遺品整理の査定風景 福岡・九州対応"
                title="天神屋 出張買取サービス — ご自宅へ無料出張・その場で現金買取"
                className="w-full h-[400px] object-cover object-top rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 to-transparent rounded-2xl" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-5 py-4">
                  <div className="text-amber-400 text-sm font-semibold mb-1">遺品整理も承ります</div>
                  <div className="text-white text-xs leading-relaxed">故人のお品物を大切に査定。ご遺族の気持ちに寄り添った丁寧な対応を心がけております。</div>
                </div>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="w-full lg:w-[55%]">
            <h3 className="text-2xl font-bold text-white mb-8" style={{ fontFamily: '\'Noto Serif JP\', serif' }}>
              福岡の買取専門店・天神屋が選ばれる理由
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feat, i) => (
                <FeatureItem key={feat.label} feat={feat} index={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StepCard({ step, index }: { step: typeof steps[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`bg-stone-800/60 border border-stone-700/50 rounded-2xl p-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="text-amber-500/40 text-5xl font-black leading-none mb-4">{step.num}</div>
      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-500/20 text-amber-400 text-xl mb-4">
        <i className={step.icon} />
      </div>
      <h3 className="text-white font-bold text-base mb-2">{step.title}</h3>
      <p className="text-stone-400 text-sm leading-relaxed">{step.desc}</p>
    </div>
  );
}

function FeatureItem({ feat, index }: { feat: typeof features[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`flex items-center gap-4 p-4 rounded-xl bg-stone-800/40 border border-stone-700/40 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-500/20 text-amber-400 text-xl shrink-0">
        <i className={feat.icon} />
      </div>
      <span className="text-white text-sm font-medium">{feat.label}</span>
    </div>
  );
}
